import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Button, useMediaQuery } from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
  docContainer: {
    width: '100%',
    height: 'calc(100vh - 160px)', // Account for header and banner height
    marginTop: '20px',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('xs')]: {
      height: 'calc(100vh - 180px)', // Account for wrapped buttons on mobile
      marginTop: '10px',
    },
  },
  iframe: {
    width: '100%',
    flex: 1,
    border: 'none',
    [theme.breakpoints.down('xs')]: {
      // Ensure iframe doesn't overflow on small devices
      height: 'calc(100vh - 200px)', // Even more conservative for very small screens
    },
  },
  navButtons: {
    display: 'flex',
    justifyContent: 'center',
    padding: '10px',
    backgroundColor: '#1a1a1a',
    borderBottom: '1px solid #4d79ff',
    flexWrap: 'wrap', // Allow buttons to wrap on narrow screens
    '& button': {
      margin: '4px',
      backgroundColor: '#222',
      color: '#fff',
      borderRadius: '25px',
      border: '1px solid #4d79ff',
      fontWeight: '600',
      transition: 'all 0.3s ease',
      minWidth: '0', // Allow buttons to shrink
      padding: '6px 12px', // Slightly smaller padding
      fontSize: '0.9rem', // Smaller text on mobile
      [theme.breakpoints.down('xs')]: {
        fontSize: '0.8rem',
        margin: '3px',
        padding: '4px 8px',
        minWidth: '0'
      },
      '&:hover': {
        backgroundColor: '#4d79ff',
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)'
      }
    }
  },
  mobileNavContainer: {
    [theme.breakpoints.down('xs')]: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      width: '100%'
    }
  }
}));

const DocPage = () => {
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const isVerySmallScreen = useMediaQuery('(max-width:340px)');
  
  // Parse the doc parameter from the URL
  const searchParams = new URLSearchParams(location.search);
  const docPath = searchParams.get('doc') || 'e3d_doc.html'; // Default to main doc if none specified
  
  // Create the full path to the doc file
  const docUrl = `${process.env.PUBLIC_URL}/${docPath}`;
  
  // Handler to navigate between different doc pages
  const navigateToDoc = (newDocPath) => {
    navigate(`/docs?doc=${newDocPath}`);
  };

  // Helper to determine if a button should have active styling
  const isActive = (path) => docPath === path;

  // Get appropriate button labels based on screen size
  const getButtonLabels = () => {
    if (isVerySmallScreen) {
      return {
        userGuide: 'User',
        whitepaper: 'Paper',
        nftGuide: 'NFT',
        swapGuide: 'Swap',
        stakeGuide: 'Stake',
        architecture: 'Arch'
      };
    }
    
    return {
      userGuide: 'User Guide',
      whitepaper: 'Whitepaper',
      nftGuide: 'NFT Guide',
      swapGuide: 'Swap Guide',
      stakeGuide: 'Stake Guide',
      architecture: 'Architecture'
    };
  };
  
  const buttonLabels = getButtonLabels();

  // Define the active style to apply
  const activeStyle = {
    backgroundColor: '#4d79ff',
    fontWeight: '700',
    boxShadow: '0 2px 6px rgba(77, 121, 255, 0.5)'
  };


  return (
    <div className={classes.docContainer}>
      <div className={classes.navButtons}>
        <div className={classes.mobileNavContainer}>
          <Button 
            onClick={() => navigateToDoc('e3d_user_guide.html')} 
            style={isActive('e3d_user_guide.html') ? activeStyle : {}}
          >{buttonLabels.userGuide}</Button>
          <Button 
            onClick={() => navigateToDoc('e3d_whitepaper.html')} 
            style={isActive('e3d_whitepaper.html') ? activeStyle : {}}
          >{buttonLabels.whitepaper}</Button>
          <Button 
            onClick={() => navigateToDoc('e3d_nft_guide.html')} 
            style={isActive('e3d_nft_guide.html') ? activeStyle : {}}
          >{buttonLabels.nftGuide}</Button>
          <Button 
            onClick={() => navigateToDoc('e3d_swap_guide.html')} 
            style={isActive('e3d_swap_guide.html') ? activeStyle : {}}
          >{buttonLabels.swapGuide}</Button>
          <Button 
            onClick={() => navigateToDoc('e3d_stake_guide.html')} 
            style={isActive('e3d_stake_guide.html') ? activeStyle : {}}
          >{buttonLabels.stakeGuide}</Button>
          <Button 
            onClick={() => navigateToDoc('e3d_architecture.html')} 
            style={isActive('e3d_architecture.html') ? activeStyle : {}}
          >{buttonLabels.architecture}</Button>
        </div>
      </div>
      
      <iframe 
        src={docUrl} 
        className={classes.iframe} 
        title="E3D Documentation"
      />
    </div>
  );
};

export default DocPage;
